/**
 * Custom hook for timeline scroll tracking and virtualization
 */

import { useState, useEffect, useCallback, type RefObject } from 'react';
import { pixelToYear } from './timeline-utils';
import { TIMELINE_LAYOUT } from './timeline-config';

interface UseTimelineVirtualizationParams {
    scrollContainerRef: RefObject<HTMLDivElement>;
    minYear: number;
    maxYear: number;
    timelineWidth: number;
}

interface UseTimelineVirtualizationReturn {
    scrollLeft: number;
    viewportWidth: number;
    visibleStartYear: number;
    visibleEndYear: number;
}

/**
 * Manages scroll state and calculates visible date range for virtualization
 */
export function useTimelineVirtualization({
    scrollContainerRef,
    minYear,
    maxYear,
    timelineWidth
}: UseTimelineVirtualizationParams): UseTimelineVirtualizationReturn {
    const [scrollLeft, setScrollLeft] = useState(0);
    const [viewportWidth, setViewportWidth] = useState(0);

    // Handle scroll events to update virtualization state
    const handleScroll = useCallback(() => {
        if (scrollContainerRef.current) {
            setScrollLeft(scrollContainerRef.current.scrollLeft);
        }
    }, [scrollContainerRef]);

    // Initial viewport measurement and scroll listener
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        setViewportWidth(container.clientWidth);
        setScrollLeft(container.scrollLeft);

        // Use requestAnimationFrame for performance
        let ticking = false;
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        container.addEventListener('scroll', onScroll, { passive: true });
        
        // Also listen for resize to update viewport width
        const onResize = () => {
            if (container) setViewportWidth(container.clientWidth);
        };
        window.addEventListener('resize', onResize);

        return () => {
            container.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
        };
    }, [scrollContainerRef, handleScroll]);

    // Calculate visible date range for virtualization
    const visibleStartYear = (() => {
        // Add buffer for smooth scrolling and to prevent flickering during zoom
        const bufferPixels = viewportWidth * TIMELINE_LAYOUT.VIRTUALIZATION_BUFFER_MULTIPLIER;
        
        const startPixel = Math.max(0, scrollLeft - bufferPixels);
        const startYear = pixelToYear(startPixel, minYear, maxYear, timelineWidth);
        
        return Math.max(minYear, startYear);
    })();

    const visibleEndYear = (() => {
        const bufferPixels = viewportWidth * TIMELINE_LAYOUT.VIRTUALIZATION_BUFFER_MULTIPLIER;
        
        const endPixel = scrollLeft + viewportWidth + bufferPixels;
        const endYear = pixelToYear(endPixel, minYear, maxYear, timelineWidth);
        
        return Math.min(maxYear, endYear);
    })();

    return {
        scrollLeft,
        viewportWidth,
        visibleStartYear,
        visibleEndYear
    };
}

